// ----------------------------------------------------------------------

import { alpha } from '@mui/material/styles';

export default function Button(theme) {
  // Safely get palette colors with fallbacks
  const getColor = (colorName) => {
    const color = theme.palette?.[colorName];
    if (!color) return null;
    return {
      main: color.main || '#569cd3',
      light: color.light || color.main,
      dark: color.dark || color.main,
      contrastText: color.contrastText || '#fff',
    };
  };

  const primary = getColor('primary');
  const secondary = getColor('secondary');
  const success = getColor('success');
  const error = getColor('error');

  return {
    MuiButton: {
      defaultProps: {
        // Map 'main' color to 'primary' for backward compatibility
        color: 'primary',
      },
      variants: [
        // Gradient Button Variant
        {
          props: { variant: 'gradient' },
          style: {
            color: '#fff',
            background: theme.gradients?.primary || 'linear-gradient(135deg, #A8469B 0%, #569cd3 100%)',
            boxShadow: `0 4px 12px ${alpha('#A8469B', 0.3)}`,
            border: 'none',
            fontWeight: 700,
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #8e3983 0%, #4788ba 100%)',
              boxShadow: `0 6px 16px ${alpha('#A8469B', 0.4)}`,
              transform: 'translateY(-2px)',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: `0 2px 8px ${alpha('#A8469B', 0.3)}`,
            },
            '&.Mui-disabled': {
              background: alpha('#919EAB', 0.3),
              color: alpha('#fff', 0.5),
            },
          },
        },
        // Glass Button Variant
        {
          props: { variant: 'glass' },
          style: {
            background: alpha('#fff', 0.15),
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: `2px solid ${alpha('#fff', 0.2)}`,
            color: theme.palette.text.primary,
            fontWeight: 600,
            transition: 'all 0.3s ease',
            '&:hover': {
              background: alpha('#fff', 0.25),
              borderColor: alpha('#fff', 0.3),
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${alpha('#000', 0.1)}`,
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          },
        },
        // Outlined Gradient Button
        {
          props: { variant: 'outlinedGradient' },
          style: {
            background: 'transparent',
            border: '2px solid transparent',
            backgroundImage: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), ${theme.gradients?.primary || 'linear-gradient(135deg, #A8469B 0%, #569cd3 100%)'}`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            color: '#A8469B',
            fontWeight: 700,
            transition: 'all 0.3s ease',
            '&:hover': {
              background: alpha('#A8469B', 0.05),
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${alpha('#A8469B', 0.2)}`,
            },
          },
        },
        // Success Gradient
        {
          props: { variant: 'gradient', color: 'success' },
          style: {
            background: theme.gradients?.success || 'linear-gradient(135deg, #AAF27F 0%, #54D62C 100%)',
            boxShadow: `0 4px 12px ${alpha('#54D62C', 0.3)}`,
            '&:hover': {
              background: 'linear-gradient(135deg, #8BD367 0%, #229A16 100%)',
              boxShadow: `0 6px 16px ${alpha('#54D62C', 0.4)}`,
            },
          },
        },
        // Error Gradient
        {
          props: { variant: 'gradient', color: 'error' },
          style: {
            background: theme.gradients?.error || 'linear-gradient(135deg, #FFA48D 0%, #FF4842 100%)',
            boxShadow: `0 4px 12px ${alpha('#FF4842', 0.3)}`,
            '&:hover': {
              background: 'linear-gradient(135deg, #FF7A6F 0%, #B72136 100%)',
              boxShadow: `0 6px 16px ${alpha('#FF4842', 0.4)}`,
            },
          },
        },
        // Warning Gradient
        {
          props: { variant: 'gradient', color: 'warning' },
          style: {
            background: theme.gradients?.warning || 'linear-gradient(135deg, #FFE16A 0%, #FFC107 100%)',
            boxShadow: `0 4px 12px ${alpha('#FFC107', 0.3)}`,
            color: theme.palette.grey[800],
            '&:hover': {
              background: 'linear-gradient(135deg, #FFD740 0%, #B78103 100%)',
              boxShadow: `0 6px 16px ${alpha('#FFC107', 0.4)}`,
            },
          },
        },
        // Info Gradient
        {
          props: { variant: 'gradient', color: 'info' },
          style: {
            background: theme.gradients?.info || 'linear-gradient(135deg, #74CAFF 0%, #1890FF 100%)',
            boxShadow: `0 4px 12px ${alpha('#1890FF', 0.3)}`,
            '&:hover': {
              background: 'linear-gradient(135deg, #3DA0E8 0%, #0C53B7 100%)',
              boxShadow: `0 6px 16px ${alpha('#1890FF', 0.4)}`,
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: theme.shape?.borderRadius * 1.5 || 9,
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        sizeLarge: {
          height: 48,
          padding: '12px 24px',
        },
        sizeMedium: {
          height: 40,
          padding: '10px 20px',
        },
        sizeSmall: {
          height: 32,
          padding: '6px 16px',
        },
        // Support for custom 'main' color prop (maps to primary)
        containedMain: {
          boxShadow: theme.customShadows?.primary || theme.shadows?.[4] || 'none',
          backgroundColor: primary?.main || '#569cd3',
          color: primary?.contrastText || '#fff',
          '&:hover': {
            boxShadow: theme.customShadows?.primary || theme.shadows?.[4] || 'none',
            backgroundColor: primary?.dark || primary?.main || '#3A7BA8',
          },
        },
        outlinedMain: {
          border: `1px solid ${primary?.main || '#569cd3'}`,
          color: primary?.main || '#569cd3',
          '&:hover': {
            border: `1px solid ${primary?.dark || '#3A7BA8'}`,
            backgroundColor: primary?.lighter || 'rgba(86, 156, 211, 0.08)',
          },
        },
        textMain: {
          color: primary?.main || '#569cd3',
          '&:hover': {
            backgroundColor: primary?.lighter || 'rgba(86, 156, 211, 0.08)',
          },
        },
        containedPrimary: {
          boxShadow: theme.customShadows?.primary || theme.shadows?.[4] || 'none',
          '&:hover': {
            boxShadow: theme.customShadows?.primary || theme.shadows?.[4] || 'none',
            backgroundColor: primary?.dark || primary?.main || '#3A7BA8',
          },
        },
        containedSecondary: {
          boxShadow: theme.customShadows?.secondary || theme.shadows?.[4] || 'none',
          '&:hover': {
            boxShadow: theme.customShadows?.secondary || theme.shadows?.[4] || 'none',
            backgroundColor: secondary?.dark || secondary?.main || '#1939B7',
          },
        },
        containedSuccess: {
          boxShadow: theme.customShadows?.success || theme.shadows?.[4] || 'none',
          '&:hover': {
            boxShadow: theme.customShadows?.success || theme.shadows?.[4] || 'none',
            backgroundColor: success?.dark || success?.main || '#229A16',
          },
        },
        containedError: {
          boxShadow: theme.customShadows?.error || theme.shadows?.[4] || 'none',
          '&:hover': {
            boxShadow: theme.customShadows?.error || theme.shadows?.[4] || 'none',
            backgroundColor: error?.dark || error?.main || '#B72136',
          },
        },
        outlinedInherit: {
          border: `1px solid ${theme.palette?.divider || '#e0e0e0'}`,
          '&:hover': {
            backgroundColor: theme.palette?.action?.hover || 'rgba(0, 0, 0, 0.04)',
          },
        },
        textInherit: {
          '&:hover': {
            backgroundColor: theme.palette?.action?.hover || 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
  };
}
